import type { FiscalPeriod, FiscalPeriodActivity } from '@erp/shared';
import { CalendarRange, History, Lock, LockOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDate, formatDateTime } from '../../lib/intl.js';

/**
 * Fiscal periods: close a month to seal it, reopen to unseal.
 *
 * Closing is enforced server-side at the GL chokepoint — nothing dated inside
 * a closed period can be posted, edited, voided or deleted, because every
 * financial mutation goes through gl.post and its period check. This page
 * only turns the key; the lock itself lives in the ledger.
 *
 * Every turn of the key is written to the audit log ('periods.close' /
 * 'periods.reopen'), and the activity card reads those rows back — so a
 * period that was quietly reopened, adjusted and re-closed leaves a visible
 * trail instead of looking permanently sealed.
 */
export function PeriodsPage(): JSX.Element {
  const { t, i18n } = useTranslation('gl');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('periods.write');
  const [periods, setPeriods] = useState<FiscalPeriod[] | null>(null);
  const [activity, setActivity] = useState<FiscalPeriodActivity[]>([]);

  const reload = async (): Promise<void> => {
    const [p, a] = await Promise.all([
      api().get<{ items: FiscalPeriod[] }>('/fiscal-periods'),
      api().get<{ items: FiscalPeriodActivity[] }>('/fiscal-periods/activity').catch(() => ({ data: { items: [] } })),
    ]);
    setPeriods(p.data.items);
    setActivity(a.data.items);
  };
  useEffect(() => { void reload(); }, []);

  const onClose = async (p: FiscalPeriod): Promise<void> => {
    if (!(await confirmDialog({
      message: t('periods.actions.confirmClose', { code: p.code, defaultValue: 'Close period {{code}}? Nothing dated inside it can be posted or edited until it is reopened.' }),
      tone: 'danger',
    }))) return;
    try {
      await api().post(`/fiscal-periods/${p.id}/close`, {});
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReopen = async (p: FiscalPeriod): Promise<void> => {
    if (!(await confirmDialog({
      message: t('periods.actions.confirmReopen', { code: p.code, defaultValue: 'Reopen period {{code}}? Postings and edits dated inside it become possible again.' }),
    }))) return;
    try {
      await api().post(`/fiscal-periods/${p.id}/reopen`, {});
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  if (!periods) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;
  if (periods.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <header>
          <h1 className="text-2xl font-semibold">{t('periods.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('periods.description')}</p>
        </header>
        <EmptyState
          icon={<CalendarRange className="h-8 w-8" aria-hidden />}
          title={t('periods.empty')}
          description={t('periods.emptyDescription')}
        />
      </div>
    );
  }

  const columns: DataTableColumn<FiscalPeriod>[] = [
    {
      id: 'code',
      header: t('periods.columns.code'),
      sortValue: (p) => p.code,
      searchValue: (p) => p.code,
      cellClassName: 'font-mono',
      cell: (p) => p.code,
    },
    {
      id: 'start',
      header: t('periods.columns.start'),
      sortValue: (p) => p.periodStart,
      cell: (p) => formatDate(p.periodStart, { locale: i18n.language }),
    },
    {
      id: 'end',
      header: t('periods.columns.end'),
      sortValue: (p) => p.periodEnd,
      cell: (p) => formatDate(p.periodEnd, { locale: i18n.language }),
    },
    {
      id: 'status',
      header: t('periods.columns.status'),
      sortValue: (p) => t(`periods.status.${p.status}`),
      searchValue: (p) => t(`periods.status.${p.status}`),
      cell: (p) => (
        <span className="inline-flex items-center gap-1.5">
          <Badge variant={p.status === 'open' ? 'success' : 'secondary'}>
            {t(`periods.status.${p.status}`)}
          </Badge>
          {p.status === 'closed' && (
            <Lock className="h-3.5 w-3.5 text-fg-subtle" aria-hidden />
          )}
        </span>
      ),
    },
    {
      id: 'closedBy',
      header: t('periods.columns.closedBy', { defaultValue: 'Closed by' }),
      sortValue: (p) => p.closedByName ?? '',
      cellClassName: 'text-xs text-fg-muted',
      cell: (p) =>
        p.status === 'closed' ? (
          <span>
            {p.closedByName ?? '—'}
            {p.closedAt ? ` · ${formatDateTime(p.closedAt, { locale: i18n.language })}` : ''}
          </span>
        ) : (
          '—'
        ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-24',
            cell: (p: FiscalPeriod) =>
              p.status === 'open' ? (
                <Button variant="ghost" size="sm" onClick={() => onClose(p)}>
                  {t('periods.actions.close')}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => onReopen(p)}>
                  {t('periods.actions.reopen', { defaultValue: 'Reopen' })}
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('periods.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('periods.description')}</p>
      </header>
      <p className="flex items-center gap-1.5 text-xs text-fg-muted">
        <Lock className="h-3.5 w-3.5 flex-none" aria-hidden />
        {t('periods.lockHint', {
          defaultValue: 'A closed period is sealed: nothing dated inside it can be posted, edited or deleted until it is reopened.',
        })}
      </p>
      <Card className="overflow-hidden p-0">
        <DataTable columns={columns} data={periods} rowKey={(p) => p.id} />
      </Card>

      {activity.length > 0 && (
        <Card className="p-0">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <History className="h-4 w-4 text-fg-muted" aria-hidden />
            <h2 className="text-sm font-semibold">{t('periods.activity.title', { defaultValue: 'Activity' })}</h2>
          </div>
          <ul>
            {activity.map((a, i) => (
              <li
                key={a.id}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm ${i === 0 ? '' : 'border-t border-border/60'}`}
              >
                {a.action === 'close'
                  ? <Lock className="h-4 w-4 flex-none text-warning" aria-hidden />
                  : <LockOpen className="h-4 w-4 flex-none text-success" aria-hidden />}
                <span className="min-w-0 grow truncate">
                  {t(`periods.activity.${a.action}`, {
                    user: a.actorName ?? t('periods.activity.someone', { defaultValue: 'Someone' }),
                    code: a.periodCode ?? '—',
                    defaultValue: a.action === 'close' ? '{{user}} closed {{code}}' : '{{user}} reopened {{code}}',
                  })}
                </span>
                <span className="flex-none text-xs text-fg-subtle">
                  {formatDateTime(a.at, { locale: i18n.language })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
